/**
 * A source that has not yet been opened, identified by its topic id and the
 * timeline range it covers.
 */
export interface PendingSource {
  topicId: string;
  startTime: bigint;
  endTime: bigint;
}

/**
 * Choose which pending source to open next, anchored to the playhead.
 *
 * The Image Panel opens its per-bag sources on demand around the playhead so
 * the first frame draws without waiting for every bag. Given the sources not
 * yet opened and the current playhead, this picks, in order of preference:
 *   1. a source whose range covers the playhead (the on-screen source),
 *   2. otherwise the nearest source starting ahead of the playhead, then
 *   3. otherwise the nearest source ending behind the playhead.
 *
 * Ahead is preferred over behind regardless of distance: forward playback is
 * the common case, so the fill stays ahead of where the user is heading.
 * Reading the live playhead on each call is what makes a seek re-anchor the
 * fill — the picks after a seek target the seek region.
 *
 * Ties are broken by input order. Returns the chosen source's index into
 * `pending` (so the caller can remove it without an O(n) rescan), or
 * `undefined` when nothing is pending.
 */
export function pickNextSource(
  pending: readonly PendingSource[],
  playheadNs: bigint,
): number | undefined {
  let bestIndex: number | undefined;
  let bestRank = Number.POSITIVE_INFINITY;
  let bestDistance = 0n;

  for (let index = 0; index < pending.length; index++) {
    const { rank, distance } = classify(pending[index], playheadNs);
    if (
      bestIndex === undefined ||
      rank < bestRank ||
      (rank === bestRank && distance < bestDistance)
    ) {
      bestIndex = index;
      bestRank = rank;
      bestDistance = distance;
    }
  }

  return bestIndex;
}

/**
 * Rank a source relative to the playhead: 0 covers, 1 ahead, 2 behind; the
 * distance orders sources within a rank (nearer first).
 */
function classify(
  source: PendingSource,
  playheadNs: bigint,
): { rank: number; distance: bigint } {
  if (source.startTime <= playheadNs && playheadNs <= source.endTime) {
    return { rank: 0, distance: 0n };
  }
  if (source.startTime > playheadNs) {
    return { rank: 1, distance: source.startTime - playheadNs };
  }
  return { rank: 2, distance: playheadNs - source.endTime };
}
